import { isInwardSupplyRow } from "../../../services/billingImportFindService";
import {
  isBranch3ImportSaleVoucherRow,
  isCreditSalesBrRow,
  isOutwardSupplyBrRow,
  isTransferInVoucherRow,
  isTransferOutNewVoucherRow,
} from "../../../services/salesImportTransactionDataService";
import {
  normalizeSalesImportBranchId,
  rowMatchesSalesImportBranch,
  salesImportBranchDisplayName,
} from "../config/salesImportBranchIds";
import {
  lineAmountOnly,
  lineBillAmountHeaderOnly,
  loadLineChunkRowsForDateRange,
  normalizeVoucherType,
  resolveImportVoucherTotal,
} from "./salesLineChunksReader";
import { brandNameFromRow } from "./brandByBranchReportService";
import { importRowSalespersonName, importRowStaffName } from "../utils/importRowFields";
import { parseTranDateLoose, rowTranDateValue, toYmd } from "../utils/dateParse";

/** Default branch for branch-scoped reports (Branch ID 10). */
export const BRANCH_REPORT_DEFAULT_BRANCH_ID = "10";

export const BRANCH_VOUCHER_REPORT_TYPES = {
  "outward-credit-br": {
    key: "outward-credit-br",
    menuLabel: "Outward Supply Br & Credit Sales Br",
    listLabel: "1. Outward Supply Br & Credit Sales Br",
    voucherLabel: "Outward Supply Br · Credit Sales Br",
    rowMatcher: (row, branchId) =>
      rowMatchesSalesImportBranch(row, branchId) && isBranch3ImportSaleVoucherRow(row),
  },
  "inward-supply": {
    key: "inward-supply",
    menuLabel: "Inward Supply (Branch)",
    listLabel: "2. Inward Supply",
    voucherLabel: "Inward Supply",
    rowMatcher: (row, branchId) => rowMatchesSalesImportBranch(row, branchId) && isInwardSupplyRow(row),
  },
  "transfer-in": {
    key: "transfer-in",
    menuLabel: "Transfer In (Branch)",
    listLabel: "3. Transfer In",
    voucherLabel: "Transfer IN",
    rowMatcher: (row, branchId) => rowMatchesSalesImportBranch(row, branchId) && isTransferInVoucherRow(row),
  },
  "transfer-out": {
    key: "transfer-out",
    menuLabel: "Transfer Out (Branch)",
    listLabel: "4. Transfer Out",
    voucherLabel: "Transfer Out - New",
    rowMatcher: (row, branchId) =>
      rowMatchesSalesImportBranch(row, branchId) && isTransferOutNewVoucherRow(row),
  },
};

export const BRANCH_VOUCHER_REPORT_TYPE_KEYS = Object.keys(BRANCH_VOUCHER_REPORT_TYPES);

export const BRANCH_VOUCHER_ALL_BRANDS = "all";
export const BRANCH_VOUCHER_NO_BRAND = "none";
export const BRANCH_VOUCHER_BLANK_BRAND = "(Blank)";

export function brandLabelFromRow(row) {
  const name = brandNameFromRow(row);
  return name || BRANCH_VOUCHER_BLANK_BRAND;
}

export function resolveBrandFilterSelection(brandName, brandName2) {
  const picks = [];
  if (brandName && brandName !== BRANCH_VOUCHER_ALL_BRANDS) picks.push(brandName);
  if (brandName2 && brandName2 !== BRANCH_VOUCHER_ALL_BRANDS && brandName2 !== BRANCH_VOUCHER_NO_BRAND) {
    picks.push(brandName2);
  }
  return [...new Set(picks)];
}

export function formatBrandFilterLabel(brands) {
  if (!brands?.length) return "All brands";
  if (brands.length === 1) return brands[0];
  return brands.join(" + ");
}

export function rowMatchesBrandFilter(row, brandName) {
  if (!brandName || brandName === BRANCH_VOUCHER_ALL_BRANDS) return true;
  return brandLabelFromRow(row) === brandName;
}

function filterRowsByBrand(rows, brandName) {
  if (!brandName || brandName === BRANCH_VOUCHER_ALL_BRANDS) return rows || [];
  return (rows || []).filter((row) => rowMatchesBrandFilter(row, brandName));
}

function filterRowsByBrands(rows, brands) {
  if (!brands?.length) return rows || [];
  return (rows || []).filter((row) => brands.includes(brandLabelFromRow(row)));
}


export function collectBranchVoucherBrandOptions(rowsByType) {
  const set = new Set();
  for (const key of BRANCH_VOUCHER_REPORT_TYPE_KEYS) {
    for (const row of rowsByType[key] || []) {
      set.add(brandLabelFromRow(row));
    }
  }
  return [...set].sort((a, b) => {
    if (a === BRANCH_VOUCHER_BLANK_BRAND) return 1;
    if (b === BRANCH_VOUCHER_BLANK_BRAND) return -1;
    return a.localeCompare(b);
  });
}

/**
 * Build report bundle from pre-loaded rows (supports brand filter without re-fetch).
 */
export function buildBranchVoucherReportsBundle(rawRowsByType, params) {
  const year = Number(params.year);
  const branchId = normalizeSalesImportBranchId(params.branchId || BRANCH_REPORT_DEFAULT_BRANCH_ID);
  const brandName = params.brandName || BRANCH_VOUCHER_ALL_BRANDS;
  const brandName2 = params.brandName2 || BRANCH_VOUCHER_NO_BRAND;
  const selectedBrands = resolveBrandFilterSelection(brandName, brandName2);

  const reports = {};
  for (const key of BRANCH_VOUCHER_REPORT_TYPE_KEYS) {
    const filtered = filterRowsByBrands(rawRowsByType[key], selectedBrands);
    reports[key] = buildReportFromRows(filtered, key, branchId, year);
  }

  return {
    meta: {
      year,
      branchId,
      branchLabel: salesImportBranchDisplayName(branchId),
      brandName,
      brandName2,
      selectedBrands,
      brandLabel: formatBrandFilterLabel(selectedBrands),
      months: buildYearMonthSlots(year),
    },
    reports,
  };
}

function matchesAnyBranchVoucherReport(row, branchId) {
  return BRANCH_VOUCHER_REPORT_TYPE_KEYS.some((key) =>
    BRANCH_VOUCHER_REPORT_TYPES[key].rowMatcher(row, branchId)
  );
}

function calendarYearRange(year) {
  const from = new Date(year, 0, 1);
  const to = new Date(year, 11, 31);
  return { fromDate: toYmd(from), toDate: toYmd(to) };
}

function buildYearMonthSlots(year) {
  return Array.from({ length: 12 }, (_, monthIndex) => {
    const monthKey = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
    return {
      monthKey,
      monthIndex,
      label: monthLabel(monthKey),
      shortLabel: new Date(year, monthIndex, 1).toLocaleString("en-IN", { month: "short" }),
    };
  });
}

function summarizeVouchers(vouchers) {
  const totalAmount = vouchers.reduce((s, v) => s + v.amount, 0);
  const totalLines = vouchers.reduce((s, v) => s + v.lineCount, 0);
  return {
    totalAmount,
    voucherCount: vouchers.length,
    lineCount: totalLines,
  };
}

function buildMonthlyTotals(vouchers, year) {
  const slots = buildYearMonthSlots(year);
  const byMonth = Object.fromEntries(slots.map((s) => [s.monthKey, { ...s, voucherCount: 0, lineCount: 0, amount: 0 }]));

  for (const voucher of vouchers) {
    const mk = monthKeyFromDate(voucher.tranDateObj);
    if (!mk || !byMonth[mk]) continue;
    byMonth[mk].voucherCount += 1;
    byMonth[mk].lineCount += voucher.lineCount;
    byMonth[mk].amount += voucher.amount;
  }

  return slots.map((s) => byMonth[s.monthKey]);
}

function buildReportFromRows(rows, key, branchId, year) {
  const config = BRANCH_VOUCHER_REPORT_TYPES[key];
  const vouchers = aggregateBranchVoucherReport(rows).map((v) => ({
    ...v,
    monthKey: monthKeyFromDate(v.tranDateObj),
  }));
  const summary = summarizeVouchers(vouchers);
  const report = {
    meta: {
      reportType: key,
      voucherLabel: config.voucherLabel,
      lineCount: rows.length,
      voucherCount: vouchers.length,
    },
    summary,
    months: buildMonthlyTotals(vouchers, year),
    breakdown: buildVoucherTypeBreakdown(vouchers),
    vouchers,
  };

  if (key === "outward-credit-br") {
    const outwardVouchers = vouchers.filter((v) => isOutwardSupplyBrRow({ voucherType: v.voucherType }));
    const creditVouchers = vouchers.filter((v) => isCreditSalesBrRow({ voucherType: v.voucherType }));
    report.subReports = {
      outward: {
        listLabel: "1a. Outward Supply Br",
        voucherLabel: "Outward Supply Br",
        summary: summarizeVouchers(outwardVouchers),
        months: buildMonthlyTotals(outwardVouchers, year),
        vouchers: outwardVouchers,
      },
      credit: {
        listLabel: "1b. Credit Sales Br",
        voucherLabel: "Credit Sales Br",
        summary: summarizeVouchers(creditVouchers),
        months: buildMonthlyTotals(creditVouchers, year),
        vouchers: creditVouchers,
      },
    };
  }

  return report;
}

function monthKeyFromDate(dateObj) {
  if (!dateObj) return "";
  return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(key) {
  if (!key) return "—";
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "short", year: "numeric" });
}

function parseVoucherLine(row) {
  const voucherNo = String(row.voucherNo ?? row.VoucherNo ?? "").trim();
  const voucherType = normalizeVoucherType(row);
  const dt = parseTranDateLoose(rowTranDateValue(row));
  return {
    voucherNo,
    voucherType,
    voucherKey: `${voucherType}::${voucherNo}`,
    invoiceDate: dt ? toYmd(dt) : "",
    tranDateObj: dt,
    customerName: String(row.customerName ?? row.CustomerName ?? "").trim(),
    employeeName: importRowSalespersonName(row),
    staffName: importRowStaffName(row),
    itemName: String(row.itemName ?? row.ItemName ?? "").trim(),
    partNo: String(row.partNo ?? row.PartNo ?? "").trim(),
    qty: Number(row.qty ?? row.Qty) || 0,
    lineAmount: Math.abs(lineAmountOnly(row)),
    billAmount: lineBillAmountHeaderOnly(row),
  };
}

/** One row per voucher (type + number) with correct line/bill totals. */
export function aggregateBranchVoucherReport(rows) {
  const byVoucher = new Map();

  for (const row of rows || []) {
    const line = parseVoucherLine(row);
    if (!line.voucherNo) continue;

    const prev = byVoucher.get(line.voucherKey);
    if (!prev) {
      byVoucher.set(line.voucherKey, {
        voucherNo: line.voucherNo,
        voucherType: line.voucherType,
        invoiceDate: line.invoiceDate,
        tranDateObj: line.tranDateObj,
        customerName: line.customerName,
        employeeName: line.employeeName,
        staffName: line.staffName,
        lineCount: 1,
        lineAmountSum: line.lineAmount,
        maxBillAmount: line.billAmount,
        items: [line],
      });
      continue;
    }

    prev.lineCount += 1;
    prev.lineAmountSum += line.lineAmount;
    if (line.billAmount > prev.maxBillAmount) prev.maxBillAmount = line.billAmount;
    if (!prev.customerName && line.customerName) prev.customerName = line.customerName;
    if (!prev.employeeName && line.employeeName) prev.employeeName = line.employeeName;
    if (!prev.staffName && line.staffName) prev.staffName = line.staffName;
    if (!prev.invoiceDate && line.invoiceDate) prev.invoiceDate = line.invoiceDate;
    prev.items.push(line);
  }

  return [...byVoucher.values()]
    .map((v) => {
      const amount = resolveImportVoucherTotal(v.lineAmountSum, v.maxBillAmount);
      return {
        voucherNo: v.voucherNo,
        voucherType: v.voucherType,
        invoiceDate: v.invoiceDate,
        tranDateObj: v.tranDateObj,
        customerName: v.customerName,
        employeeName: v.employeeName,
        staffName: v.staffName,
        lineCount: v.lineCount,
        amount,
        items: v.items,
      };
    })
    .sort((a, b) => {
      if (a.invoiceDate !== b.invoiceDate) return (b.invoiceDate || "").localeCompare(a.invoiceDate || "");
      if (a.voucherType !== b.voucherType) return a.voucherType.localeCompare(b.voucherType);
      return a.voucherNo.localeCompare(b.voucherNo, undefined, { numeric: true });
    });
}

function buildVoucherTypeBreakdown(vouchers) {
  const map = new Map();
  for (const v of vouchers) {
    const key = v.voucherType || "—";
    const prev = map.get(key) || { voucherType: key, voucherCount: 0, lineCount: 0, amount: 0 };
    prev.voucherCount += 1;
    prev.lineCount += v.lineCount;
    prev.amount += v.amount;
    map.set(key, prev);
  }
  return [...map.values()].sort((a, b) => b.amount - a.amount);
}

/**
 * Load all four branch voucher reports for a full year in one pass.
 * @param {{
 *   year: number,
 *   branchId?: string,
 *   onProgress?: (rows: number, chunks?: number) => void,
 * }} params
 */
export async function fetchBranchVoucherReportsBundle(params) {
  const year = Number(params.year);
  const branchId = normalizeSalesImportBranchId(params.branchId || BRANCH_REPORT_DEFAULT_BRANCH_ID);
  if (!branchId) throw new Error("Branch is required.");

  const selectedRange = calendarYearRange(year);

  const { rows, chunksLoaded } = await loadLineChunkRowsForDateRange(
    selectedRange,
    (rowCount, chunks) => params.onProgress?.(rowCount, chunks),
    { rowFilter: (row) => matchesAnyBranchVoucherReport(row, branchId) }
  );

  const yearRows = rows.filter((row) => {
    const dt = parseTranDateLoose(rowTranDateValue(row));
    return dt && dt.getFullYear() === year;
  });

  const rowsByType = Object.fromEntries(BRANCH_VOUCHER_REPORT_TYPE_KEYS.map((key) => [key, []]));
  for (const row of yearRows) {
    for (const key of BRANCH_VOUCHER_REPORT_TYPE_KEYS) {
      if (BRANCH_VOUCHER_REPORT_TYPES[key].rowMatcher(row, branchId)) {
        rowsByType[key].push(row);
        break;
      }
    }
  }

  const brandOptions = collectBranchVoucherBrandOptions(rowsByType);
  const brandName = params.brandName || BRANCH_VOUCHER_ALL_BRANDS;
  const brandName2 = params.brandName2 || BRANCH_VOUCHER_NO_BRAND;
  const built = buildBranchVoucherReportsBundle(rowsByType, { year, branchId, brandName, brandName2 });

  return {
    ...built,
    rawRowsByType: rowsByType,
    meta: {
      ...built.meta,
      fromDate: selectedRange.fromDate,
      toDate: selectedRange.toDate,
      lineCount: yearRows.length,
      chunksLoaded,
      brandOptions,
    },
  };
}

/**
 * @param {{
 *   reportType: string,
 *   year: number,
 *   monthIndex: number,
 *   branchId?: string,
 *   onProgress?: (rows: number, chunks?: number) => void,
 * }} params
 */
export async function fetchBranchVoucherReport(params) {
  const config = BRANCH_VOUCHER_REPORT_TYPES[params.reportType];
  if (!config) throw new Error("Unknown report type.");

  const year = Number(params.year);
  const monthIndex = Number(params.monthIndex);
  const branchId = normalizeSalesImportBranchId(params.branchId || BRANCH_REPORT_DEFAULT_BRANCH_ID);
  if (!branchId) throw new Error("Branch is required.");

  const from = new Date(year, monthIndex, 1);
  const to = new Date(year, monthIndex + 1, 0);
  const selectedRange = { fromDate: toYmd(from), toDate: toYmd(to) };
  const selectedMonthKey = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;

  const rowFilter = (row) => config.rowMatcher(row, branchId);

  const { rows, chunksLoaded } = await loadLineChunkRowsForDateRange(
    selectedRange,
    (rowCount, chunks) => params.onProgress?.(rowCount, chunks),
    { rowFilter }
  );

  const monthRows = rows.filter((row) => {
    const dt = parseTranDateLoose(rowTranDateValue(row));
    return monthKeyFromDate(dt) === selectedMonthKey;
  });

  const report = buildReportFromRows(monthRows, config.key, branchId, year);

  return {
    meta: {
      reportType: config.key,
      year,
      monthIndex,
      monthLabel: monthLabel(selectedMonthKey),
      branchId,
      branchLabel: salesImportBranchDisplayName(branchId),
      voucherLabel: config.voucherLabel,
      fromDate: selectedRange.fromDate,
      toDate: selectedRange.toDate,
      lineCount: monthRows.length,
      voucherCount: report.summary.voucherCount,
      chunksLoaded,
    },
    summary: report.summary,
    breakdown: report.breakdown,
    vouchers: report.vouchers,
  };
}

export function flattenBranchVoucherReportForExport(report, { monthKey } = {}) {
  let vouchers = report?.vouchers || [];
  if (monthKey && monthKey !== "all") {
    vouchers = vouchers.filter((v) => v.monthKey === monthKey);
  }
  return vouchers.map((v) => ({
    VoucherNo: v.voucherNo,
    Date: v.invoiceDate,
    Month: v.monthKey || "",
    VoucherType: v.voucherType,
    Customer: v.customerName,
    Employee: v.employeeName,
    LineCount: v.lineCount,
    Amount: v.amount,
  }));
}

export function flattenBranchVoucherMonthwiseGridForExport(bundle) {
  if (!bundle?.reports) return [];

  const monthSlots = bundle.meta?.months || [];

  const rows = buildBranchVoucherMonthwiseRows(bundle).map((row) => {
    const out = { Report: row.label };
    for (const month of row.report.months || []) {
      const slot = monthSlots.find((s) => s.monthKey === month.monthKey);
      const label = slot?.shortLabel || month.monthKey;
      out[`${label} Amount`] = month.amount;
      out[`${label} Invoices`] = month.voucherCount;
    }
    out["Year Amount"] = row.report.summary.totalAmount;
    out["Year Invoices"] = row.report.summary.voucherCount;
    return out;
  });

  return [
    {
      Report: `Year ${bundle.meta.year}`,
      Branch: bundle.meta.branchLabel,
      Brands: bundle.meta.brandLabel || "All brands",
    },
    ...rows,
  ];
}

export function flattenBranchVoucherMonthlyForExport(bundle) {
  const rows = [];
  for (const key of BRANCH_VOUCHER_REPORT_TYPE_KEYS) {
    const config = BRANCH_VOUCHER_REPORT_TYPES[key];
    const report = bundle.reports[key];

    const pushReportRows = (label, data) => {
      for (const month of data.months || []) {
        rows.push({
          Brand: bundle.meta.brandLabel || "All brands",
          Report: label,
          Month: month.label,
          Vouchers: month.voucherCount,
          Lines: month.lineCount,
          Amount: month.amount,
        });
      }
      rows.push({
        Brand: bundle.meta.brandLabel || "All brands",
        Report: label,
        Month: "Year total",
        Vouchers: data.summary.voucherCount,
        Lines: data.summary.lineCount,
        Amount: data.summary.totalAmount,
      });
    };

    pushReportRows(config.listLabel, report);
    if (report.subReports) {
      pushReportRows(report.subReports.outward.listLabel, report.subReports.outward);
      pushReportRows(report.subReports.credit.listLabel, report.subReports.credit);
    }
  }
  return rows;
}

/** Resolve active report slice for list view (supports outward/credit sub-reports). */
export function resolveBranchVoucherViewReport(bundle, viewKey) {
  if (!bundle?.reports) return null;

  if (viewKey === "outward-credit-br:outward") {
    return bundle.reports["outward-credit-br"]?.subReports?.outward || null;
  }
  if (viewKey === "outward-credit-br:credit") {
    return bundle.reports["outward-credit-br"]?.subReports?.credit || null;
  }
  return bundle.reports[viewKey] || null;
}

export function buildBranchVoucherMonthwiseRows(bundle) {
  if (!bundle?.reports) return [];
  const rows = [];
  for (const key of BRANCH_VOUCHER_REPORT_TYPE_KEYS) {
    const report = bundle.reports[key];
    const config = BRANCH_VOUCHER_REPORT_TYPES[key];
    rows.push({
      viewKey: key,
      label: config.listLabel,
      report,
      indent: 0,
    });

    if (key === "outward-credit-br" && report.subReports) {
      rows.push({
        viewKey: "outward-credit-br:outward",
        label: report.subReports.outward.listLabel,
        report: report.subReports.outward,
        indent: 1,
      });
      rows.push({
        viewKey: "outward-credit-br:credit",
        label: report.subReports.credit.listLabel,
        report: report.subReports.credit,
        indent: 1,
      });
    }
  }
  return rows;
}
